import { TelemetryInfo } from "@/hooks/use-telemetry-info";
import { MOBILE_BREAKPOINT_PX } from "@repo/common/constants";
import mixpanel from "mixpanel-browser";
import {
  normalizePath,
  buildAuthProperties,
  buildEventProperties,
  buildStandalonePageProperties,
  buildEmbeddedViewProperties,
} from "./mixpanel-properties";
import type { EmbeddedViewConfig } from "./mixpanel-properties";

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;

// Client-only module state — safe because this file is only imported in
// browser bundles (never in SSR/getServerSideProps). Do not import server-side.
let mixpanelInitialized = false;

// Cached between trackUser() and subsequent track/trackPageView calls so
// auth properties (user_id, company_*, etc.) are included automatically.
let currentTelemetryInfo: TelemetryInfo | null = null;

// ---------------------------------------------------------------------------
// Browser helpers
// ---------------------------------------------------------------------------

/**
 * Extract device_source from referrer.
 * Returns normalized path from referrer URL, or null if no referrer.
 */
const getDeviceSource = (): string | null => {
  if (typeof window === "undefined") return null;

  const referrer = document.referrer;
  if (!referrer) return null;

  try {
    const url = new URL(referrer);
    return normalizePath(url.pathname);
  } catch {
    return null;
  }
};

const getEnv = (): string => process.env.NEXT_PUBLIC_ENV || "development";

/**
 * Same breakpoint as `useMobileSize()`/`useMediaQuery` — read directly via
 * `matchMedia` rather than the hook, since this module runs outside React.
 */
const getIsMobile = (): boolean | null => {
  if (typeof window === "undefined" || !window.matchMedia) return null;
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches;
};

// ---------------------------------------------------------------------------
// Core tracking functions
// ---------------------------------------------------------------------------

export const initMixpanel = (): boolean => {
  if (process.env.NEXT_PUBLIC_ENV !== "production") {
    return false;
  }

  if (mixpanelInitialized) return true;

  if (!MIXPANEL_TOKEN) {
    console.warn("Mixpanel token is missing! Check your .env file.");
    return false;
  }

  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      track_pageview: false,
      autocapture: { pageview: false },
    });

    mixpanel.register({ "App ID": "crm-modern" });
    mixpanelInitialized = true;
    return true;
  } catch (error) {
    console.error("Failed to initialize Mixpanel:", error);
    return false;
  }
};

/**
 * Track an embedded view load event.
 * Fires "Embedded View Loaded" — used for pages loaded inside an iframe within the PHP CRM.
 */
type TrackEmbeddedViewParams = {
  normalizedPath: string;
  config: EmbeddedViewConfig;
};

export const trackEmbeddedView = ({
  normalizedPath,
  config,
}: TrackEmbeddedViewParams) => {
  const initialized = initMixpanel();
  if (!initialized) return;

  const properties = buildEventProperties({
    telemetryInfo: currentTelemetryInfo,
    deviceSource: getDeviceSource(),
    isMobile: getIsMobile(),
    env: getEnv(),
    eventProperties: buildEmbeddedViewProperties({ config, normalizedPath }),
  });

  mixpanel.track("Embedded View Loaded", properties);
};

/**
 * Track a standalone page view. Fires "Page Loaded".
 * Called by TelemetryProvider on mount and route changes for non-embedded pages.
 */
export const trackPageView = (url: string) => {
  const initialized = initMixpanel();
  if (!initialized) return;

  const pathname = url.split(/[?#]/)[0] || url;
  const normalizedPath = normalizePath(pathname);
  const deviceSource = getDeviceSource();

  // Skip if current path equals referrer (prevents duplicate on page reload)
  if (normalizedPath === deviceSource) {
    return;
  }

  const properties = buildEventProperties({
    telemetryInfo: currentTelemetryInfo,
    deviceSource,
    isMobile: getIsMobile(),
    env: getEnv(),
    eventProperties: buildStandalonePageProperties({
      config: null,
      normalizedPath,
      deviceSource,
    }),
  });

  mixpanel.track("Page Loaded", properties);
};

export const track = (eventName: string, properties?: Record<string, any>) => {
  const initialized = initMixpanel();
  if (!initialized) {
    console.warn("Skipping track, not initialized:", eventName);
    return;
  }

  try {
    mixpanel.track(
      eventName,
      buildEventProperties({
        telemetryInfo: currentTelemetryInfo,
        deviceSource: getDeviceSource(),
        isMobile: getIsMobile(),
        env: getEnv(),
        eventProperties: properties,
      }),
    );
  } catch (error) {
    console.error(`Failed to track ${eventName}:`, error);
  }
};

export const trackUser = (telemetryInfo: TelemetryInfo) => {
  const initialized = initMixpanel();
  if (!initialized || !telemetryInfo.user) return;

  currentTelemetryInfo = telemetryInfo;

  try {
    const { user } = telemetryInfo;
    const userId = `cli_${user.id}`;

    mixpanel.identify(userId);

    const authProps = buildAuthProperties(telemetryInfo);

    mixpanel.people.set({
      $name: `${user.firstName} ${user.lastName}`,
      $email: user.email,
      user_type: authProps.user_type,
      user_role: authProps.user_role,
      company_id: authProps.company_id,
      company_name: authProps.company_name,
      company_email: authProps.company_email,
      company_package_name: authProps.company_package_name,
      company_package_level: authProps.company_package_level,
      company_is_active: authProps.company_is_active,
    });
  } catch (error) {
    console.error("Failed to track user:", error);
  }
};
